import { ChangeEvent, useRef, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Field,
  FieldLabel,
  Icon,
  Input,
  LoadingIcon,
  Separator,
  Stack,
  Switch,
  Text,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { getSourceTypeLabel } from './citation';
import { UploadedFile } from './types';
import { useUploadPdf } from './usePdfUpload';

interface KnowledgeSourcesProps {
  tenantId: string;
  initialSources: string[];
  initialFiles: UploadedFile[];
  onSave: (sources: string[]) => Promise<void>;
  onSaveFiles: (files: UploadedFile[]) => Promise<void>;
  showFileUploads?: boolean;
}

const AVAILABLE_SOURCES = ['unsignedNotes'];

export const KnowledgeSources = ({
  tenantId,
  initialSources,
  initialFiles,
  onSave,
  onSaveFiles,
  showFileUploads = true,
}: KnowledgeSourcesProps) => {
  const [sources, setSources] = useState<string[]>(initialSources);
  const [files, setFiles] = useState<UploadedFile[]>(initialFiles);
  const [isSaving, setIsSaving] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const userId = useUserIdFromSession();
  const apiClient = useApiClient();
  const toast = useToast();
  const { t } = useTranslation();

  const handleToggle = async (source: string) => {
    const newSources = sources.includes(source)
      ? sources.filter((s) => s !== source)
      : [...sources, source];
    setIsSaving(true);
    try {
      await onSave(newSources);
      setSources(newSources);
    } catch {
      toast.error(t('chat.knowledgeSources.updateFailed'));
      setSources(sources);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUploadComplete = async (file: UploadedFile) => {
    const newFiles = [...files, file];
    setFiles(newFiles);

    setIsSaving(true);
    try {
      await onSaveFiles(newFiles);
    } catch {
      toast.error(t('chat.knowledgeSources.saveFileFailed'));
      setFiles(files);
    } finally {
      setIsSaving(false);
    }
  };

  const { uploadFile, isUploading } = useUploadPdf({
    tenantId,
    onUploadComplete: handleFileUploadComplete,
  });

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
    event.target.value = '';
  };

  const handleRemoveFile = async (fileId: string) => {
    const newFiles = files.filter((f) => f.id !== fileId);
    setFiles(newFiles);

    setIsSaving(true);
    try {
      await onSaveFiles(newFiles);
    } catch {
      toast.error(t('chat.knowledgeSources.removeFileFailed'));
      setFiles(files);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadFile = async (file: UploadedFile) => {
    try {
      setDownloadingFileId(file.id);

      if (file.fileUrl) {
        window.open(file.fileUrl, '_blank');
        return;
      }

      const getFileResponse = await apiClient.users.getUserFileSignedUrl({
        params: { tenantId, userId, fileId: file.id },
      });

      if (getFileResponse.status !== 200) {
        throw new Error('Failed to get file download URL');
      }

      const presignedUrl = getFileResponse.body.presignedUrl;
      if (!presignedUrl) {
        throw new Error('File URL not available');
      }

      // Open file in new tab
      window.open(presignedUrl, '_blank');
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('chat.knowledgeSources.downloadFailed');
      toast.error(errorMessage);
    } finally {
      setDownloadingFileId(null);
    }
  };
  return (
    <>
      {AVAILABLE_SOURCES.map((source, i) => (
        <div key={source}>
          <Field orientation="horizontal" className="px-6 py-7">
            <FieldLabel>{getSourceTypeLabel(source)}</FieldLabel>
            <Switch
              size="sm"
              className="data-unchecked:bg-border-tertiary"
              checked={sources.includes(source)}
              onCheckedChange={() => handleToggle(source)}
              disabled={isSaving}
              aria-label={getSourceTypeLabel(source)}
            />
          </Field>
          {(i < AVAILABLE_SOURCES.length - 1 || showFileUploads) && (
            <Separator className="my-0" />
          )}
        </div>
      ))}

      {showFileUploads && (
        <Stack gap={3} align="stretch" className="px-6 py-7">
          <Stack
            gap={2}
            direction="horizontal"
            justify="between"
            align="center"
          >
            <Text size="sm" weight="semibold" color="primary">
              {t('chat.knowledgeSources.uploadedFiles')}
            </Text>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              disabled={isUploading}
              className="hidden"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleFileUploadClick}
              disabled={isSaving || isUploading}
              loading={isUploading}
            >
              <Icon name="Upload" size="auto" />
              {t('chat.knowledgeSources.uploadFiles')}
            </Button>
          </Stack>

          {files.length === 0 ? (
            <Text size="xs" weight="medium" color="tertiary">
              {t('chat.knowledgeSources.noFiles')}
            </Text>
          ) : (
            <Stack gap={2}>
              {files.map((file) => {
                const isDownloading = downloadingFileId === file.id;
                return (
                  <Stack
                    key={file.id}
                    className="cursor-pointer rounded-sm border border-border-primary bg-bg-primary p-2 hover:bg-bg-secondary"
                    onClick={() => !isDownloading && handleDownloadFile(file)}
                    gap={2}
                    direction="horizontal"
                    align="center"
                    justify="between"
                  >
                    <Stack gap={2} direction="horizontal" align="center">
                      {isDownloading ? (
                        <LoadingIcon size="lg" />
                      ) : (
                        <Icon name="FileText" size="lg" />
                      )}
                      <Text inline size="xs" className="truncate">
                        {file.fileName}
                      </Text>
                    </Stack>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(file.id);
                      }}
                      disabled={isSaving || isDownloading}
                    >
                      <Icon name="X" size="auto" />
                    </Button>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
      )}
    </>
  );
};
